import AsyncStorage from '@react-native-async-storage/async-storage';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';

const getReducerPersistBlacklist = (reducer: string): Array<string> => {
  switch (reducer) {
    case 'rental':
      // digitalFuelStations is fetched per session (car position changes);
      // a persisted list would keep stale/empty stations across restarts.
      return [
        'rentalViolations',
        'finishRentalViolations',
        'digitalFuelStations',
      ];

    case 'bookings':
      return ['bookingViolations'];

    default:
      return [];
  }
};

const getReducerPersistWhitelist = (reducer: string): Array<string> => {
  switch (reducer) {
    case 'map':
      return ['walkthroughVersion', 'showHomeWT', 'userAddress'];

    case 'damages':
      return ['newDamages'];

    case 'appData':
      return ['areas', 'serviceTypes', 'common', 'features'];

    case 'ui':
      return ['firstSteps'];

    case 'carsSearch':
      return ['circleId'];

    case 'networkStatus':
      return ['getHistory'];

    default:
      return [];
  }
};

const storage = AsyncStorage;

const LOCALSTORAGE_KEY = 'applicationData';

// Safe JSON parse that returns null on error
const safeJsonParse = (json: string | null): any => {
  if (!json) return {};
  try {
    return JSON.parse(json);
  } catch (error) {
    console.error(
      '[Redux Persist] Failed to parse stored state, clearing corrupted data:',
      error
    );
    return null; // Return null to indicate corruption
  }
};

const storageWrapper = {
  getItem: async (key: string): Promise<string | null> => {
    try {
      const allItemsJson = await storage.getItem(LOCALSTORAGE_KEY);
      const allItems = safeJsonParse(allItemsJson);

      // If parse failed (corrupted), clear storage and return null
      if (allItems === null) {
        console.warn('[Redux Persist] Clearing corrupted storage');
        await storage.removeItem(LOCALSTORAGE_KEY);
        return null;
      }

      // Try to get the item from allObjects first
      if (Object.prototype.hasOwnProperty.call(allItems, key)) {
        return allItems[key];
      }
      // If not present in allObjects, read directly from storage
      return await storage.getItem(key);
    } catch (error) {
      console.error('[Redux Persist] Error reading storage:', error);
      return null;
    }
  },

  setItem: async (key: string, item: string): Promise<void> => {
    try {
      const allItemsJson = await storage.getItem(LOCALSTORAGE_KEY);
      const allItems = safeJsonParse(allItemsJson) || {};

      // Update the item in allObjects
      allItems[key] = item;
      await storage.setItem(LOCALSTORAGE_KEY, JSON.stringify(allItems));

      // Delete the item from top-level storage if it exists
      const topLevelItem = await storage.getItem(key);
      if (topLevelItem !== null) {
        await storage.removeItem(key);
      }
    } catch (error) {
      console.error('[Redux Persist] Error writing to storage:', error);
    }
  },

  removeItem: async (key: string): Promise<void> => {
    try {
      const allItemsJson = await storage.getItem(LOCALSTORAGE_KEY);
      const allItems = safeJsonParse(allItemsJson) || {};

      // Remove the item from allObjects
      delete allItems[key];
      await storage.setItem(LOCALSTORAGE_KEY, JSON.stringify(allItems));

      // Additionally, ensure the item is removed from top-level storage
      await storage.removeItem(key);
    } catch (error) {
      console.error('[Redux Persist] Error removing from storage:', error);
    }
  },
};

export const getPersistConfig = (reducer: string): any => {
  const config: any = {
    key: reducer,
    storage: storageWrapper,
    stateReconciler: autoMergeLevel2,
  };
  const blacklist = getReducerPersistBlacklist(reducer);
  const whitelist = getReducerPersistWhitelist(reducer);

  if (blacklist.length > 0) {
    config.blacklist = blacklist;
  }

  if (whitelist.length > 0) {
    config.whitelist = whitelist;
  }

  return config;
};
